<template>
  <div>
    <div class="child-row__title pb-4">Optimisation summary</div>

    <easy-data-table
      ref="dataTable"
      table-class-name="vue3-easy-data-table table-shadow summary-table"
      :server-items-length="serverItemsLength"
      :loading="loading"
      :headers="headers"
      :items="items"
      :prevent-context-menu-row="false"
      :fixed-header="false"
      no-hover
      alternating
      hide-footer
    >
      <template #loading>
        <b-spinner class="mt-5" variant="success" />
      </template>
      <template #no-data>
        <div style="text-align: center">No data available</div>
      </template>
      <template #item="{ column, item }: { column: string; item: OptimisationPath }">
        <span v-if="column === 'icon'">
          <i v-if="item.active" class="fa fa-check" />
        </span>
        <span v-if="column === 'carrierHandle'">
          <CarrierRowWithIcon v-if="item.carrierHandle" :carrier="item.carrierHandle" :only-icon="false" />
          <span v-else>Info message</span>
          <span v-if="isFallback(item.metadata)" class="badge bg-warning ms-2">Fallback</span>
        </span>
        <span v-if="column === 'price'">
          {{ formattedPrice(item.price) }}
        </span>
        <span v-if="column === 'info'">
          <span v-if="isAddressUnavailable(item.metadata)" class="d-flex align-items-center gap-2">
            <i class="fa fa-times-circle text-danger w-auto" />
            <span class="text-danger fw-semibold">{{ addressUnavailableMessage }}</span>
          </span>
          <span v-else-if="isFallback(item.metadata)" class="d-flex align-items-center gap-2">
            <i class="fa fa-random text-warning" />
            <span class="text-warning fw-semibold">Fallback only</span>
            <template v-for="(entry, idx) in fallbackExtraMetadata(item.metadata)" :key="idx">
              <span class="badge bg-secondary ms-1">{{ entry.key }}</span>
              <span>{{ entry.value }}</span>
            </template>
          </span>
          <span v-else style="display:grid;grid-template-columns:auto 1fr;gap:4px 10px;align-items:baseline">
            <template v-for="(entry, idx) in parseMetadata(item.metadata)" :key="idx">
              <span v-if="entry.key !== null" class="badge bg-secondary">{{ entry.key }}</span>
              <span v-else style="grid-column:1/-1">{{ entry.value }}</span>
              <span v-if="entry.key !== null">{{ entry.value }}</span>
            </template>
          </span>
        </span>
      </template>
    </easy-data-table>
  </div>
</template>

<script lang="ts">
import type { Item } from '@/types/Models/Item'
import type { OptimisationPath, Shipment } from '@/types/Models/Shipment'
import { BSpinner } from 'bootstrap-vue-next'
import { ref, onMounted, type PropType, type Ref, type ComputedRef, computed } from 'vue'
import { defineComponent } from 'vue'
import { CARRIERS_NAMES } from '@/const/Carriers'
import { formattedPrice } from '@/services/Helpers'
import { useAssignmentSummaryStore } from '@/store/assignmentSummary'
import { useShipmentStore } from '@/store/shipment'
import { parseMetadata, isFallback, fallbackExtraMetadata, isAddressUnavailable } from '@/utils/optimisationMetadata'
import EasyDataTable, { type Header } from 'vue3-easy-data-table'
import CarrierRowWithIcon from '@/views/Components/Assignment/CarrierRowWithIcon.vue'

export default defineComponent({
  name: 'OptimisationSummary',
  components: { BSpinner, EasyDataTable, CarrierRowWithIcon },
  props: {
    shipment: {
      type: Object as PropType<Shipment | Item>,
      required: true,
    },
  },
  setup(props) {
    const shipmentStore = useShipmentStore()
    const assignmentSummaryStore = useAssignmentSummaryStore()

    const dataTable: Ref = ref()
    const loading: ComputedRef<boolean> = computed(() => shipmentStore.optimisationPathsLoading)
    const items: ComputedRef<OptimisationPath[]> = computed(() =>
      shipmentStore.optimisationPathsList(props.shipment.id),
    )
    const serverItemsLength: ComputedRef<number> = computed(() =>
      shipmentStore.optimisationPathsTotal(props.shipment.id),
    )

    const isSenderBoxAddress: ComputedRef<boolean> = computed(() => {
      const senderAddress = assignmentSummaryStore.assignment?.senderAddress
      if (!senderAddress) return false
      const addressLine = senderAddress.addressLine ?? ''
      return /^\s*(p\.?\s*o\.?\s*)?box\s+\d/i.test(addressLine)
    })

    const addressUnavailableMessage = computed(() => {
      const hasSenderData = !!assignmentSummaryStore.assignment?.senderAddress
      if (!hasSenderData) return 'Address unavailable for this carrier'
      return isSenderBoxAddress.value
        ? 'Sender box address unavailable for this carrier'
        : 'Recipient address unavailable or invalid'
    })

    const headers: Header[] = [
      { text: '', value: 'icon' },
      { text: 'Carrier', value: 'carrierHandle' },
      { text: 'Price', value: 'price' },
      { text: 'Debug info', value: 'info' },
    ]

    const carrierName = (carrierHandle: string) => (!carrierHandle ? '' : CARRIERS_NAMES[carrierHandle])

    onMounted(() => {
      const isShipment = (obj: unknown): obj is Shipment =>
        typeof obj === 'object' && obj !== null && 'optimisationSummary' in obj

      const summary = isShipment(props.shipment) ? (props.shipment.optimisationSummary ?? []) : []
      shipmentStore.fetchOptimisationSummary(props.shipment.id, summary)
    })

    return {
      items,
      loading,
      headers,
      dataTable,
      serverItemsLength,
      formattedPrice,
      carrierName,
      parseMetadata,
      isFallback,
      fallbackExtraMetadata,
      isAddressUnavailable,
      addressUnavailableMessage,
    }
  },
})
</script>
